import React, { useEffect, useRef, useState } from 'react'; 
import './style.scss'; 

import { HoverText } from '../../shared/TextFx';
import planeImg from '../../assets/time-section-img.png';
import serviceImage1 from '../../assets/service-image1.jpg';
import serviceImage2 from '../../assets/service-image2.jpg';
import serviceImage3 from '../../assets/service-image3.jpg';
import serviceImage4 from '../../assets/service-image4.jpg';
import serviceImage5 from '../../assets/service-image5.jpg';
import serviceImage6 from '../../assets/service-image6.jpg';

const SERVICES = [
  {
    title: 'On Board Courier',
    img: serviceImage1,
    text: 'A dedicated courier travels with your critical shipment on the same flight, personally managing the movement from origin through destination.',
  },
  {
    title: 'Hybrid On Board Courier',
    img: serviceImage2,
    text: 'Hybrid On Board Courier solutions for critical movements that require a combination of accompanied and coordinated air transport.',
  },
  {
    title: 'Next Flight Out',
    img: serviceImage3,
    text: 'Cargo moves on the earliest viable commercial routing when every hour counts.',
  },
  {
    title: 'NFO Express',
    img: serviceImage4,
    text: 'End to end Time Critical Express with dedicated terminal handling and active operational coordination throughout the movement.',
  },
  {
    title: 'Air Charter',
    img: serviceImage5,
    text: 'Dedicated aircraft when commercial schedules, shipment requirements or urgency demand another option.',
  },
  {
    title: 'Customs Brokerage',
    img: serviceImage6,
    text: 'Customs support coordinated as part of the critical movement, helping shipments continue across borders and toward their required delivery time.',
  },
];

const TITLE = 'Specialised Solutions';
const SUBTITLE = 'How We Make It Happen';
const HEADING = 'The right move for the deadline.';
const DESCRIPTION =
  'Every urgent shipment is different. Esquire identifies the fastest viable route, coordinates the movement and stays accountable through delivery.';
const DESKTOP_QUERY = '(min-width: 993px)';
const AUTOPLAY_MS = 5000;

const getIsDesktop = () =>
  typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches;

const ServicesSec = () => {
  const [active, setActive] = useState(0);
  const [isDesktop, setIsDesktop] = useState(getIsDesktop);
  const [heroVisible, setHeroVisible] = useState(false);
  const [boardInView, setBoardInView] = useState(false);

  const heroRef = useRef(null);
  const scrollRef = useRef(null);
  const boardRef = useRef(null);
  const tabsRef = useRef(null);
  const tabRefs = useRef([]);

  // Track the desktop breakpoint (sticky scroll only runs above 992px).
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Reveal the yellow intro band once, when it scrolls into view.
  useEffect(() => {
    const node = heroRef.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setHeroVisible(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeroVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  // Know whether the tab board is on screen (used by mobile autoplay).
  useEffect(() => {
    const node = boardRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([entry]) => setBoardInView(entry.isIntersecting), {
      threshold: 0.3,
    });
    io.observe(node);
    return () => io.disconnect();
  }, []);

  // Desktop: the board is pinned and scroll progress picks the active tab.
  // Hovering a tab overrides it until scroll crosses into the next segment.
  useEffect(() => {
    if (!isDesktop) return undefined;
    let ticking = false;
    let lastIndex = -1;

    const update = () => {
      ticking = false;
      const el = scrollRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const distance = rect.height - window.innerHeight;
      const progress = distance > 0 ? Math.min(1, Math.max(0, -rect.top / distance)) : 0;
      const index = Math.min(SERVICES.length - 1, Math.floor(progress * SERVICES.length));
      if (index !== lastIndex) {
        lastIndex = index;
        setActive(index);
      }
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [isDesktop]);

  // Mobile/tablet: cycle through services while the board is visible.
  // Any tap resets the timer because the effect re-runs when `active` changes.
  useEffect(() => {
    if (isDesktop || !boardInView) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    const t = setTimeout(() => setActive((i) => (i + 1) % SERVICES.length), AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [active, isDesktop, boardInView]);

  // Mobile: keep the active chip visible in the horizontal tab strip
  // (scrolls the strip only, never the page).
  useEffect(() => {
    if (isDesktop) return;
    const strip = tabsRef.current;
    const tab = tabRefs.current[active];
    if (!strip || !tab) return;
    strip.scrollTo({
      left: tab.offsetLeft - (strip.clientWidth - tab.clientWidth) / 2,
      behavior: 'smooth',
    });
  }, [active, isDesktop]);

  const onTabKeyDown = (e, i) => {
    const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
    if (!(e.key in keys)) return;
    e.preventDefault();
    const next = (i + keys[e.key] + SERVICES.length) % SERVICES.length;
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  const service = SERVICES[active];

  return (
    <section className="services" id="services">
      {/* ---------- Intro band ---------- */}
      <div className={`svc-hero${heroVisible ? ' is-in' : ''}`} ref={heroRef}>
        <div className="svc-hero__row">
          <div className="svc-hero__text">
            <h2 className="svc-hero__title h-display svc-anim" style={{ '--svc-delay': '0s' }}>
              {TITLE}
            </h2>

            <p className="svc-subtitle svc-anim" style={{ '--svc-delay': '0.12s' }}>
              <span className="svc-subtitle__text">{SUBTITLE}</span>
              <span className="svc-subtitle-bar" aria-hidden="true" />
            </p>

            <h3 className="svc-sub-heading svc-anim" style={{ '--svc-delay': '0.24s' }}>
              {HEADING}
            </h3>

            <p className="svc-para-desc svc-anim" style={{ '--svc-delay': '0.36s' }}>
              {DESCRIPTION}
            </p>
          </div>
          <div className="svc-hero__media">
            <img src={planeImg} alt="" />
          </div>
        </div>
      </div>

      {/* ---------- Tabs board ---------- */}
      <div
        className="svc-board-scroll"
        ref={scrollRef}
        style={{ '--svc-count': SERVICES.length }}
      >
        <div className="svc-board" ref={boardRef}>
          <div className="svc-board__row">
            <div
              className="svc-tabs"
              role="tablist"
              aria-label="Our services"
              aria-orientation={isDesktop ? 'vertical' : 'horizontal'}
              ref={tabsRef}
            >
              {SERVICES.map((s, i) => (
                <button
                  key={s.title}
                  type="button"
                  role="tab"
                  id={`svc-tab-${i}`}
                  aria-selected={active === i}
                  aria-controls="svc-panel"
                  tabIndex={active === i ? 0 : -1}
                  className={`svc-tab${active === i ? ' is-active' : ''}`}
                  ref={(el) => {
                    tabRefs.current[i] = el;
                  }}
                  onMouseEnter={() => isDesktop && setActive(i)}
                  onClick={() => setActive(i)}
                  onKeyDown={(e) => onTabKeyDown(e, i)}
                >
                  {s.title}
                </button>
              ))}
            </div>

            <div
              className="svc-panel"
              id="svc-panel"
              role="tabpanel"
              aria-labelledby={`svc-tab-${active}`}
            >
              <h3 className="svc-panel__title svc-panel__anim" key={`t-${active}`}>
                {service.title}
              </h3>

              {/* All images stay mounted and crossfade, so switching never flashes. */}
              <div className="svc-panel__media">
                {SERVICES.map((s, i) => (
                  <img
                    key={s.title}
                    src={s.img}
                    alt={active === i ? s.title : ''}
                    aria-hidden={active !== i}
                    className={active === i ? 'is-active' : ''}
                  />
                ))}
              </div>

              <div className="svc-panel__body svc-panel__anim" key={`b-${active}`}>
                <p className="svc-panel__text">{service.text}</p>
              </div>

              <div className="svc-panel__cta">
                <a className="about__btn text-hover btn" href="#">
                  <span className="btn__inner">
                    <HoverText words="Explore Our Solutions" />
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSec;
